import { useEffect, useId, useRef } from 'react';
import { AnimatePresence, LazyMotion, domAnimation, m, useReducedMotion } from 'motion/react';
import { Icon } from './Icon';
import './registrationExperience.css';

export type ProcessingStage = 'validating' | 'creating_pass' | 'preparing_payment';

interface ProcessingModalProps {
  open: boolean;
  stage: ProcessingStage;
  errorMessage?: string | null;
  onRetry?: () => void;
  onDismiss?: () => void;
}

const STAGES: Array<{ id: ProcessingStage; label: string; detail: string }> = [
  { id: 'validating', label: 'Validando seus dados', detail: 'Conferindo as informações com segurança.' },
  { id: 'creating_pass', label: 'Criando sua inscrição', detail: 'Gerando seu código individual de entrada.' },
  { id: 'preparing_payment', label: 'Preparando o pagamento', detail: 'Conectando sua inscrição à InfinitePay.' },
];

function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(
    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  ));
}

export function ProcessingModal({
  open,
  stage,
  errorMessage,
  onRetry,
  onDismiss,
}: ProcessingModalProps): JSX.Element {
  const titleId = useId();
  const detailId = useId();
  const dialogRef = useRef<HTMLElement>(null);
  const reduceMotion = useReducedMotion();
  const currentIndex = Math.max(0, STAGES.findIndex((item) => item.id === stage));
  const currentStage = STAGES[currentIndex]!;

  useEffect(() => {
    if (!open) return undefined;

    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.requestAnimationFrame(() => dialogRef.current?.focus());

    const onKeyDown = (event: KeyboardEvent) => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      if (event.key === 'Escape' && onDismiss) {
        event.preventDefault();
        onDismiss();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusables = focusableElements(dialog);
      if (focusables.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [onDismiss, open]);

  return (
    <LazyMotion features={domAnimation} strict>
      <AnimatePresence>
        {open ? (
          <m.div
            className="fte-processing-backdrop"
            role="presentation"
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.18 }}
          >
            <m.section
              ref={dialogRef}
              className={`fte-processing-modal ${errorMessage ? 'fte-processing-modal--error' : ''}`}
              role="dialog"
              aria-modal="true"
              aria-busy={!errorMessage}
              aria-labelledby={titleId}
              aria-describedby={detailId}
              tabIndex={-1}
              initial={reduceMotion ? false : { opacity: 0, y: 18, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 330, damping: 28 }}
            >
              <div className="fte-processing-modal__header">
                <span className="eyebrow">SALVANDO SUA INSCRIÇÃO</span>
                {onDismiss ? (
                  <button type="button" className="fte-icon-button" onClick={onDismiss} aria-label="Fechar processamento">
                    <Icon name="close" />
                  </button>
                ) : null}
              </div>

              <div className="fte-processing-core" aria-hidden="true">
                <m.span
                  className="fte-processing-core__ring"
                  animate={reduceMotion ? undefined : { rotate: 360 }}
                  transition={reduceMotion ? undefined : { duration: 2.4, ease: 'linear', repeat: Infinity }}
                />
                <span className="fte-processing-core__center">12H</span>
              </div>

              {errorMessage ? (
                <div className="fte-processing-modal__error" role="alert">
                  <span className="eyebrow">NÃO FOI POSSÍVEL CONCLUIR</span>
                  <h2 id={titleId}>Seus dados continuam aqui.</h2>
                  <p id={detailId}>{errorMessage}</p>
                  <div className="fte-processing-modal__actions">
                    {onRetry ? (
                      <button type="button" className="button button--primary" onClick={onRetry}>
                        <Icon name="refresh" /> Tentar novamente
                      </button>
                    ) : null}
                    {onDismiss ? (
                      <button type="button" className="button button--ghost" onClick={onDismiss}>Voltar ao formulário</button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <>
                  <div className="fte-processing-modal__copy" aria-live="polite" aria-atomic="true">
                    <h2 id={titleId}>{currentStage.label}</h2>
                    <p id={detailId}>{currentStage.detail}</p>
                  </div>
                  <div className="fte-processing-progress" aria-hidden="true">
                    <m.span
                      key={stage}
                      initial={reduceMotion ? false : { scaleX: Math.max(0.08, currentIndex / STAGES.length) }}
                      animate={{ scaleX: (currentIndex + 1) / STAGES.length }}
                      transition={{ duration: reduceMotion ? 0 : 0.28, ease: 'easeOut' }}
                    />
                  </div>
                  <ol className="fte-processing-steps" aria-label="Progresso da inscrição">
                    {STAGES.map((item, index) => {
                      const state = index < currentIndex ? 'complete' : index === currentIndex ? 'current' : 'upcoming';
                      return (
                        <li key={item.id} className={`is-${state}`} aria-current={state === 'current' ? 'step' : undefined}>
                          <span aria-hidden="true">{index < currentIndex ? <Icon name="check" /> : index + 1}</span>
                          {item.label}
                        </li>
                      );
                    })}
                  </ol>
                </>
              )}
            </m.section>
          </m.div>
        ) : null}
      </AnimatePresence>
    </LazyMotion>
  );
}
